import React from 'react';
import { Navigate } from 'react-router-dom';
import { useSubscription } from '@/contexts/subscription';
import { useAuth } from '@/contexts/auth';
import { useRole } from '@/contexts/RoleContext';

interface TrialGuardProps {
  children: React.ReactNode;
}

/**
 * TrialGuard - Componente que protege rotas baseado no status de assinatura
 * 
 * Modelo Freemium:
 * - Usuários Free têm acesso às funcionalidades básicas
 * - Não há mais redirecionamento obrigatório para trial
 * - Admins têm acesso total
 */
export default function TrialGuard({ children }: TrialGuardProps) {
  const { user } = useAuth();
  const { subscribed, isLoading, tier } = useSubscription();
  const { isAdmin, isLoading: roleLoading } = useRole();

  // Se não está logado, redireciona para login
  if (!user) {
    if (import.meta.env.DEV) console.log('[TrialGuard] No user, redirecting to /auth');
    return <Navigate to="/auth" replace />;
  }

  // Se ainda está carregando, delega ao skeleton do LazyRoute
  if (isLoading || roleLoading) {
    if (import.meta.env.DEV) {
      console.log('[TrialGuard] Loading subscription data...', { 
        subscriptionLoading: isLoading, 
        roleLoading,
        subscribed,
        tier
      });
    }
    return null;
  }

  // Se é admin, pula verificações
  if (isAdmin) {
    if (import.meta.env.DEV) console.log('[TrialGuard] User is admin, allowing access');
    return <>{children}</>;
  }

  if (import.meta.env.DEV) {
    console.log('[TrialGuard] User has access', { 
      tier,
      subscribed,
      isLoading 
    });
  }
  
  return <>{children}</>;
}
